import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, fetchCached } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Image, BookOpen, Mail, ArrowRight, DollarSign, CalendarCheck,
  FileText, User, Clock, MailOpen
} from 'lucide-react';
import { AdminPageHeader, AdminSurface, AdminEmptyState } from '@/components/admin/AdminPrimitives';

const STAT_STYLES = {
  amber: 'bg-amber-50 text-amber-600',
  blue: 'bg-blue-50 text-[#2D5BE3]',
  violet: 'bg-violet-50 text-violet-600',
  teal: 'bg-teal-50 text-teal-600',
  green: 'bg-green-50 text-green-600',
};

export default function AdminDashboard() {
  const { admin, hasPermission, isSuperAdmin } = useAuth();
  const canViewGallery = hasPermission('gallery');
  const canViewBlog = hasPermission('blog');
  const canViewMessages = hasPermission('messages');
  const canViewActivityRegistrations = hasPermission('activity_registrations');
  const canManageDonations = hasPermission('donations');
  const canViewDonationStats = isSuperAdmin();
  const [stats, setStats] = useState({ gallery: 0, blog: 0, messages: 0, unread: 0, donations: 0, donationAmount: 0, activityRegistrations: 0, newActivityRegistrations: 0 });
  const [recentMessages, setRecentMessages] = useState([]);
  const [upcomingActivities, setUpcomingActivities] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/admin/dashboard-stats');
        setStats(data);
      } catch {}
    };
    fetchStats();
  }, [canViewActivityRegistrations, canViewBlog, canViewDonationStats, canViewGallery, canViewMessages]);

  useEffect(() => {
    if (!canViewMessages) return;
    api.get('/admin/messages').then(({ data }) => setRecentMessages((data || []).slice(0, 3))).catch(() => {});
  }, [canViewMessages]);

  useEffect(() => {
    fetchCached('/content/activities').then((data) => {
      setUpcomingActivities((data?.activities || []).slice(0, 4));
    }).catch(() => {});
  }, []);

  const cards = [
    { title: 'Messages', value: stats.messages, icon: Mail, link: '/admin/messages', style: 'amber', badge: stats.unread, show: canViewMessages },
    { title: 'Registrations', value: stats.activityRegistrations, icon: CalendarCheck, link: '/admin/activity-registrations', style: 'blue', badge: stats.newActivityRegistrations, show: canViewActivityRegistrations },
    { title: 'Gallery Images', value: stats.gallery, icon: Image, link: '/admin/gallery', style: 'violet', show: canViewGallery },
    { title: 'Blog Posts', value: stats.blog, icon: BookOpen, link: '/admin/blog', style: 'teal', show: canViewBlog },
    { title: 'Donations', value: `$${stats.donationAmount.toFixed(2)}`, icon: DollarSign, link: '/admin/donations', style: 'green', show: canViewDonationStats },
  ].filter((c) => c.show);

  const quickActions = [
    { label: 'Edit Home Content', to: '/admin/content', icon: FileText, show: hasPermission('content') },
    { label: 'Manage Gallery', to: '/admin/gallery', icon: Image, show: canViewGallery },
    { label: 'Create Blog Post', to: '/admin/blog', icon: BookOpen, show: canViewBlog },
    { label: 'View Registrations', to: '/admin/activity-registrations', icon: CalendarCheck, show: canViewActivityRegistrations },
    { label: 'Update Founder Info', to: '/admin/founder', icon: User, show: hasPermission('founder') },
    { label: 'View Messages', to: '/admin/messages', icon: Mail, show: canViewMessages },
  ].filter((a) => a.show);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const emailLocal = (admin?.email || '').split('@')[0] || '';
  const greetingName = emailLocal ? emailLocal.charAt(0).toUpperCase() + emailLocal.slice(1) : 'Admin';

  return (
    <div data-testid="admin-dashboard">
      <AdminPageHeader
        eyebrow="Ministry Operations"
        title="Dashboard"
        description={`Welcome back, ${greetingName} — a calm overview of content, conversations, registrations, and giving activity.`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.title}
              to={card.link}
              data-testid={`dashboard-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="mscm-admin-surface mscm-admin-stat-card mscm-admin-card-link block p-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D5BE3]/30"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`mscm-admin-stat-icon ${STAT_STYLES[card.style]}`}>
                  <Icon size={20} />
                </div>
                {card.badge > 0 && (
                  <span className="bg-[#EF4444] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {card.badge}
                  </span>
                )}
              </div>
              <p className="mscm-admin-stat-value-lg">{card.value}</p>
              <p className="mt-1 text-sm font-medium text-[#64748B]">{card.title}</p>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Messages */}
        {canViewMessages && (
          <AdminSurface className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Recent Messages
              </h2>
              <Link to="/admin/messages" className="text-xs font-semibold text-[#2D5BE3] hover:underline">
                View all
              </Link>
            </div>
            {recentMessages.length === 0 ? (
              <AdminEmptyState icon={Mail} title="No messages yet" description="New contact form submissions will appear here." />
            ) : (
              <div className="space-y-2">
                {recentMessages.map((msg) => (
                  <Link
                    key={msg.id}
                    to="/admin/messages"
                    className="mscm-admin-list-item flex items-start gap-3 rounded-[8px] border border-[#E2E8F0]/70 bg-white p-3"
                  >
                    <span className="mscm-admin-user-avatar shrink-0" style={{ background: 'linear-gradient(135deg, #2D5BE3, #6B8AFF)' }}>
                      {(msg.name || '?').slice(0, 2).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-[#0F172A]">{msg.name}</p>
                        {!msg.read && (
                          <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-600">New</span>
                        )}
                      </div>
                      <p className="truncate text-xs text-[#64748B]">{msg.message}</p>
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-[#94A3B8]">
                        {msg.read ? <MailOpen size={10} /> : <Mail size={10} />} {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </AdminSurface>
        )}

        {/* Upcoming Activities */}
        <AdminSurface className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Upcoming Activities
            </h2>
            {canViewActivityRegistrations && (
              <Link to="/admin/activity-registrations" className="text-xs font-semibold text-[#2D5BE3] hover:underline">
                View registrations
              </Link>
            )}
          </div>
          {upcomingActivities.length === 0 ? (
            <AdminEmptyState icon={CalendarCheck} title="No activities listed" description="Add activities from Site Content to see them here." />
          ) : (
            <div className="space-y-2">
              {upcomingActivities.map((activity, i) => (
                <div key={`${activity.title}-${i}`} className="flex items-center gap-3 rounded-[8px] border border-[#E2E8F0]/70 bg-white p-3">
                  <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-[8px] bg-[#EBF2FA] text-[#2D5BE3]">
                    <span className="text-[10px] font-bold uppercase leading-none">{activity.month}</span>
                    <span className="text-sm font-extrabold leading-none">{activity.day || '•'}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#0F172A]">{activity.title}</p>
                    <p className="truncate text-xs text-[#64748B]">{activity.date}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AdminSurface>
      </div>

      {/* Quick Actions */}
      <AdminSurface className="p-6">
        <h2 className="text-xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Quick Actions
        </h2>
        <p className="mb-5 text-sm text-[#64748B]">Jump directly into the most common ministry updates.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.to}
                to={action.to}
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="mscm-admin-action-link flex min-h-[5.5rem] flex-col items-start justify-between gap-3 rounded-[12px] border border-[#E2E8F0]/70 bg-[#F8FBFF] p-4 text-sm font-semibold text-[#475569] hover:bg-[#EBF2FA] hover:text-[#2D5BE3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D5BE3]/30"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-white text-[#2D5BE3] shadow-[0_4px_12px_rgba(11,31,77,0.08)]">
                  <Icon size={16} />
                </span>
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="leading-tight">{action.label}</span>
                  <ArrowRight size={14} className="shrink-0" />
                </span>
              </Link>
            );
          })}
        </div>
      </AdminSurface>
    </div>
  );
}
